import React, {useState, useEffect} from 'react';

import TextInput from '../Form/TextInput/TextInput';
import {gql} from 'apollo-boost';
import FormattedInput from '../Form/TextInput/FormattedInput';
import Modal from '../Modal/Modal';
import Button from '../Form/Button/Button';
import withFeatureFlags from '../FeatureFlags/withFeatureFlags';
import SelfieStepup from '../SelfieStepup/SelfieStepup';
import modulusCheck from '../../utils/modulusCheck';
import {emptyString, sortCodeRegex, accountNumberRegex, formatSortCode} from '../../utils/helpers';
import {useMutation} from '@apollo/react-hooks';
import {useLogger} from '../../contexts/Logger';
import infoIcon from '../../assets/images/info-fraud.svg';
import warningIcon from '../../assets/images/warning-fraud.svg';
import Section from "../MyLinkedAccount/Section1";
import {useUnifiedAnalytics} from "../../contexts/AnalyticsWrapper";

/** @import {WithFeatureFlagsProps} from '../FeatureFlags/FeatureFlagsTypes' */
/** @import {LinkedAccountFraudWarningModalProps, UpdateLinkedAccountProps, Answer} from './UpdateLinkedAccountTypes' */

const SAVE_LINKED_ACCOUNT = gql`
  mutation saveLinkedAccount($request : SaveLinkedAccountRequestType!) {
    saveLinkedAccount(request: $request) {
      success
    }
  }
`;

const SAVE_LINKED_ACCOUNT2 = gql`
  mutation saveLinkedAccount2($request : SaveLinkedAccountRequestType!) {
    saveLinkedAccount2(request: $request) {
      success
    }
  }
`;

const SAVE_CUSTOMER_FRAUD_WARNING = gql`
  mutation saveCustomerFraudWarning($request: SaveCustomerFraudWarningRequestType!) {
    saveCustomerFraudWarning(request: $request) {
      success
    }
  }
`;

function UpdateLinkedAccount(/** @type {UpdateLinkedAccountProps & WithFeatureFlagsProps} */ props) {
    const linkedAccount = props.profile.linkedAccount;
    const [editing, setEditing] = useState(false);
    const [sortCode, setSortCode] = useState(linkedAccount.sortCode);
    const [accountNumber, setAccountNumber] = useState(linkedAccount.accountNumber);
    const [showValidation, setShowValidation] = useState(false);
    const [showBasicValidation, setBasicValidation] = useState(false);
    const [showSortcodeValidation, setSortcodeValidation] = useState(false);
    const [allowSave, setAllowSave] = useState(false);
    const [saveLinkedAccount] = useMutation(SAVE_LINKED_ACCOUNT);
    const [saveLinkedAccount2] = useMutation(SAVE_LINKED_ACCOUNT2);
    const [saveCustomerFraudWarning] = useMutation(SAVE_CUSTOMER_FRAUD_WARNING);
    const [answers, setAnswers] = useState(/** @type { Answer[] } */ ([]));
    const logger = useLogger();

    const [showModals, setShowModals] = useState({
        stepUp: false,
        fraudWarning: false
    });

    const { trackPopupViewedEvent, trackPopupClickedEvent, trackPopupClosedEvent } = useUnifiedAnalytics();

    // to be set by SelfieStepup component
    /** @type {null | (() => void)} */
    let selfieStepupInit = null;

    // validation effect
    useEffect(() => {
        const valid = basicValidation();
        setAllowSave(valid);

        // Disable scrolling only when the modal is active
        if (showModals.stepUp || showModals.fraudWarning) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [sortCode, accountNumber, showModals]);

    const cancelEditing = () => {
        setEditing(false);
        setSortCode(linkedAccount.sortCode);
        setAccountNumber(linkedAccount.accountNumber);
    };

    const approveEditing = async () => {
        trackPopupViewedEvent(
            'fraud',
            'Why are you changing your linked account',
            'Investec-Save-secure|roadblock_load|why are you changing your linked account',
            'Fraud check_Why are you changing your linked account'
        );

        setShowModals({
            stepUp: false,
            fraudWarning: true
        });
    };

    const validate = async () => {
        const sort = sortCode?.replace(/-/g, '').replace(/\s/g, '');
        let isValid = true

        setSortcodeValidation(false);
        setBasicValidation(false);

        //check to verify the entered bank sort code is DS sort code
        if (sort === '406460' && props.featureEnabled('EnableValidationOnLinkedAccountWithDSSortCode')) {
            isValid = false;
            setSortcodeValidation(true);
        }

        if (isValid) {
            isValid = Boolean(basicValidation() && accountNumber && sort &&
                await modulusCheck(accountNumber, sort));
            setBasicValidation(true);
        }

        if (!isValid)
            setShowValidation(true);

        return isValid;
    };

    const basicValidation = () => {
        const sort = sortCode?.replace(/-/g, '').replace(/\s/g, '');
        return !emptyString(sort) &&
            !emptyString(accountNumber) &&
            sortCodeRegex.test(sort || '') &&
            accountNumberRegex.test(accountNumber || '');
    }

    const startEditing = () => {
        setSortCode('');
        setAccountNumber('');
        setSortcodeValidation(false);
        setBasicValidation(false);
        selfieStepupInit && selfieStepupInit();
    };

    const renderReadOnly = () => {
        return (
            <>
                <div className="my-account__wrapper-horizontal">
                    <p className="my-account__wrapper-horizontal__description">This is the only account you can make deposits from and withdrawals to. It must be a UK current account held in your name.</p>
                    <TextInput className="my-account__input" value={formatSortCode(sortCode)} label='Sort code'
                               disabled/>
                    <TextInput className="my-account__input" value={accountNumber} label='Account number' disabled/>
                </div>
            </>
        );
    };

    const sortCodeChange = (/** @type {string} */ val) => {
        setSortCode(val);
        setShowValidation(false);
    };

    const accountNumberChange = (/** @type {string} */ val) => {
        setAccountNumber(val);
        setShowValidation(false);
    };

    const cancelOnFraudWarning = () => {
        trackPopupClickedEvent(
            'fraud',
            'Why are you changing your linked account',
            'Cancel',
            'my-linked-account|roadblock_click|Cancel',
            'Fraud check_Why are you changing your linked account'
        );

        setShowModals({
            stepUp: showModals.stepUp,
            fraudWarning: false
        });

        setSortCode(linkedAccount.sortCode);
        setAccountNumber(linkedAccount.accountNumber);
        cancelEditing();
    };

    const onFraudWarningProceed = () => {
        trackPopupClickedEvent(
            'fraud',
            'Why are you changing your linked account - Onfido Journey',
            'Cancel',
            'my-linked-account|roadblock_click|Cancel',
            'Fraud check_Why are you changing your linked account - Onfido Journey'
        );
        setShowModals({
            stepUp: true,
            fraudWarning: false
        });
    };

    const cancelStepup = () => {
        trackPopupClosedEvent(
            'fraud',
            'Why are you changing your linked account',
            'my-linked-account|roadblock_click|close',
            'Fraud check_Why are you changing your linked account'
        );

        setShowModals({
            stepUp: false,
            fraudWarning: showModals.fraudWarning
        });
        setSortCode(linkedAccount.sortCode);
        setAccountNumber(linkedAccount.accountNumber);
        cancelEditing();
    };

    const saveComplete = () => {
        setShowModals({
            stepUp: false,
            fraudWarning: false
        });

        setEditing(false);

        const sort = sortCode?.replace(/-/g, '').replace(/\s/g, '');
        setSortCode(sort);
    }

    const save = async (/** @type {string} */ flowId) => {
        const sort = sortCode?.replace(/-/g, '').replace(/\s/g, '');

        let success = false;

        if (!props.featureEnabled('FixLinkedAccountUpdatesPhase2')) {

            const accounts = props.profile.linkedAccount.accountIds.map(a => {
                return {
                    accountNumber: accountNumber,
                    sortCode: sort,
                    accountId: a
                }
            });

            logger.info(`Saving linked account update`, {
                sortCode: sort,
                accountNumber: accountNumber,
                accounts: accounts.length,
                flowId: flowId
            });

            const linkedAccount = {
                accountNumber: accountNumber,
                sortCode: sort,
            };

            const result = await saveLinkedAccount({
                variables: {
                    request: {
                        accounts: accounts,
                        flowId: flowId,
                        linkedAccount: linkedAccount
                    }
                }
            });

            //Save customer fraud warning
            const fraudWarningResult = await saveCustomerFraudWarning({
                variables: {
                    request: {
                        customerFraudWarning: {
                            fraudWarningOnPhoneWithBank: answers[0].FraudWarningOnPhoneWithBank,
                            fraudWarningMoveMoneyBank: answers[1].FraudWarningMoveMoneyBank,
                            fraudWarningMoveMoneyOtherOrg: answers[2].FraudWarningMoveMoneyOtherOrg
                        }
                    }
                }
            });

            success = result.data.saveLinkedAccount.success && fraudWarningResult.data.success;
        } else {
            logger.info(`Saving linked account update`, {
                sortCode: sort,
                accountNumber: accountNumber,
                flowId: flowId
            });

            const linkedAccount = {
                accountNumber: accountNumber,
                sortCode: sort,
            };

            const result = await saveLinkedAccount2({
                variables: {
                    request: {
                        flowId: flowId,
                        linkedAccount: linkedAccount
                    }
                }
            });

            //Save customer fraud warning
            const fraudWarningResult = await saveCustomerFraudWarning({
                variables: {
                    request: {
                        customerFraudWarning: {
                            fraudWarningOnPhoneWithBank: answers[0].FraudWarningOnPhoneWithBank,
                            fraudWarningMoveMoneyBank: answers[1].FraudWarningMoveMoneyBank,
                            fraudWarningMoveMoneyOtherOrg: answers[2].FraudWarningMoveMoneyOtherOrg
                        }
                    }
                }
            });

            success = result.data.saveLinkedAccount2.success && fraudWarningResult.data.success;
        }

        logger.info(`Saving linked account ${success ? 'succeeded' : 'failed'} (${success})`);

        return success;
    }

    const renderEditForm = () => {
        return (
            <>
                <div className="my-account__wrapper-horizontal">
                    <p className="my-account__wrapper-horizontal__description">This is the only account you can make deposits from and withdrawals to. It must be a UK current account held in your name.</p>
                    <FormattedInput
                        options={{
                            numericOnly: true,
                            blocks: [2, 2, 2],
                            delimiter: ' - ',
                        }}
                        className="my-account__input"
                        placeholder="Sort code"
                        value={sortCode}
                        label='&nbsp;'
                        onChange={sortCodeChange}
                        type="tel"
                    />
                    <FormattedInput
                        options={{
                            numericOnly: true,
                            blocks: [8]
                        }}
                        className="my-account__input"
                        placeholder="Account number"
                        value={accountNumber}
                        label='&nbsp;'
                        onChange={accountNumberChange}
                        type="tel"
                    />
                    <div
                        className={showValidation && showSortcodeValidation ? "login__error" : "login__error login__error--hide"}>
                        <span className="login__error--wrap">Please choose a UK current account - this looks like an Investec Save account.</span>
                    </div>
                    <div
                        className={showValidation && showBasicValidation ? "login__error" : "login__error login__error--hide"}>
                        <span className="login__error--wrap">Looks like this sort code and account number is not recognised.</span>
                    </div>
                </div>
            </>
        );
    };

    return (
        <>
            <Section
                title="My Linked account"
                editing={editing}
                setEditing={setEditing}
                onCancel={cancelEditing}
                onSave={approveEditing}
                onStart={startEditing}
                modifiers={["blue-boxed"]}
                validation={async () => await validate()}
                hideEditing={!props.featureEnabled('EnableLinkedAccountEditing')}
                valid={allowSave}
                closeEditingOnConfirm={false}>
                {editing ? renderEditForm() : renderReadOnly()}
            </Section>

            {showModals.fraudWarning &&
                <LinkedAccountFraudWarningModal
                    setVisible={cancelOnFraudWarning}
                    setAnswers={setAnswers}
                    onProceed={onFraudWarningProceed}
                    answers={answers}
                />
            }

            <SelfieStepup
                setInit={init => selfieStepupInit = init}
                visible={showModals.stepUp}
                context="LINKED_ACCOUNT_CHANGE"
                title="Please verify your identity"
                onCancel={cancelStepup}
                onComplete={saveComplete}
                onUserVerified={save}
                mobileNumber={props.profile.mobileNumber}
                successCopy={{
                    title: 'Request received!',
                    body: "We're busy updating your linked account details. We’ll let you know when this is done. You’ll then be able to move money to and from your <b>new</b> linked account."
                }}
                failureCopy={{
                    title: " Update failed",
                    bodyFirstParagraph: "We're not able to update your linked account as we’ve been unable to verify who you are. This can happen when:"
                }}
                onBlockedUrl="/linked-account/locked"/>
        </>
    );
};

const questions = [
    {
        question: 'Why are you changing your linked account?',
        intro: true,
        message: 'Please choose the most accurate responses to the following questions, to help us protect you from potential scams. <br/><br/>If someone tells you to choose the wrong answer, stop. They’re trying to scam you.'
    },
    {
        question: 'Are you currently on the phone with your bank?',
        name: 'FraudWarningOnPhoneWithBank',
        yesInfo: {
            type: 'info',
            message: 'Criminals can change their caller ID to make it appear on your phone as though the call is coming from your bank, on a number you recognise. <br/><br/>If you\'re unsure, you can always end the conversation and call back using a number you\'ve sourced independently.<br/><br/>If you need to speak with us, you can contact us via live chat.'
        },
        noInfo: {
            type: 'info',
            message: 'There are genuine reasons we might call you. However, we\'ll usually speak with you via live chat.'
        }
    },
    {
        question: 'Has someone contacted you claiming to be from your bank, telling you that you need to move your money to a safe account?',
        name: 'FraudWarningMoveMoneyBank',
        yesInfo: {
            type: 'warning',
            message: '<span style="font-size: 18px">This is a scam. Hang up and call us on 0330 123 5985.</span><br/><br/>Investec will never call you out of the blue to tell you that your money is at risk, and that you need to move it to another account.'
        },
        noInfo: {
            type: 'info',
            message: '\'Safe account\' scams are where criminals steal your money by getting you to move it to another account that they control.<br><br>They do this by calling you out of the blue, pretending to be us, and claiming your money is at risk and you need to move it urgently. They may go on to ask about other accounts that you hold with other banks and advise you to move money from those accounts too.'
        }
    },
    {
        question: 'Has someone contacted you claiming to be the police, HMRC or another trusted organisation, and told you to move your money?',
        name: 'FraudWarningMoveMoneyOtherOrg',
        yesInfo: {
            type: 'warning',
            message: '<span style="font-size: 18px">This is a scam. Hang up and call us on 0330 123 5985.</span><br/><br/>No legitimate authority will ever call you out of the blue to tell you that your money is at risk and you need to move it to another account, or make a bank transfer on the spot.'
        },
        noInfo: {
            type: 'info',
            message: 'Criminals pretending to be the police may claim your money is at risk, or ask you to help catch a criminal by moving your money. Alternatively, a criminal posing as a trusted authority like HMRC might demand that you pay taxes you owe. They may tell you there\'s a warrant for your arrest or other frightening consequences if you do not act quickly.<br><br>For more advice, you can call us on 0330 123 5985.'
        }
    }
];

function LinkedAccountFraudWarningModal(/** @type {LinkedAccountFraudWarningModalProps} */ props) {
    const cancel = () => {
        props.setVisible(false);
    };

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswer, setSelectedAnswer] = useState(/** @type {boolean | null} */ (null));
    const { trackPopupClickedEvent, trackPopupClosedEvent } = useUnifiedAnalytics();
    
    const handleAnswer = (/** @type {boolean} */ answer) => {
        setSelectedAnswer(answer);
    };

    const nextQuestion = () => {
        const updatedAnswers = [...props.answers];
        const currentQuestion = questions[currentQuestionIndex].question;
        const currentQuestionName = questions[currentQuestionIndex].name;
        
        setSelectedAnswer(null);
        if (currentQuestionIndex < questions.length - 1) {
            if (currentQuestionIndex !== 0) {
                // @ts-expect-error - computed property name
                updatedAnswers.push({[currentQuestionName]: selectedAnswer});
                props.setAnswers(updatedAnswers);
                if (currentQuestionIndex === 2 && selectedAnswer === true) {
                    // @ts-expect-error - computed property name
                    updatedAnswers.push({ [questions[currentQuestionIndex + 2].name]: null });
                }
            }
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const renderInfoContent = (/** @type {{type: string; message: string} | undefined} */ info) => {
        if (!info) { return null; }
        const icon = info.type === 'warning' ? warningIcon : infoIcon;
        const className = info.type === 'warning' ? 'warning' : 'info';

        return (
            <div className={className}>
                <div className="info-content">
                    <img src={icon} alt="Icon" className="info-icon" />
                    <div dangerouslySetInnerHTML={{ __html: info.message }} />
                </div>
            </div>
        );
    };

    const renderQuestion = () => {
        if (questions[currentQuestionIndex].intro) {
            return (
                <div className="modal-content">
                    <div className="header-wrapper-wrapper">
                        <div className="header-wrapper">
                            <div className="header2">
                                <h3 className="why-are-you" dangerouslySetInnerHTML={{ __html: questions[currentQuestionIndex].question }} />
                            </div>
                        </div>
                    </div>
                    <div className="modal-content-inner">
                        <div className="header-container">
                            <div className="header1">
                                <div className="please-choose-the-container">
                                    <div className="please-choose-the" dangerouslySetInnerHTML={{ __html: questions[currentQuestionIndex].message }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else {
            return (
                <>
                    <h3 className="question">{questions[currentQuestionIndex].question}</h3>
                    <div className="answers">
                        <label className={`answer-button ${selectedAnswer === true ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="answer"
                                value="true"
                                checked={selectedAnswer === true}
                                onChange={() => handleAnswer(true)}
                            />
                            <span>Yes</span>
                        </label>
                        {selectedAnswer === true && renderInfoContent(questions[currentQuestionIndex].yesInfo)}
                        <label className={`answer-button ${selectedAnswer === false ? 'selected' : ''}`}>
                            <input
                                type="radio"
                                name="answer"
                                value="false"
                                checked={selectedAnswer === false}
                                onChange={() => handleAnswer(false)}
                            />
                            <span>No</span>
                        </label>
                        {selectedAnswer === false && renderInfoContent(questions[currentQuestionIndex].noInfo)}
                    </div>
                </>
            );
        }
    };

    const handleProceed = () => {
        const currentQuestionName = questions[currentQuestionIndex].name;
        // @ts-expect-error - computed property name
        const updatedAnswers = [...props.answers, { [currentQuestionName]: selectedAnswer }];

        if (currentQuestionName !== undefined && selectedAnswer != null) {
            props.setAnswers(updatedAnswers);
        }
        if (currentQuestionIndex === 2 && selectedAnswer === true) {
            // @ts-expect-error - computed property name
            updatedAnswers.push({ [questions[currentQuestionIndex + 1].name]: null });
            trackPopupClickedEvent(
                'fraud',
                `Question ${currentQuestionIndex} - ${questions[currentQuestionIndex].question}`,
                'Proceed',
                "my-linked-account|roadblock_click|Proceed",
                `Fraud check_${questions[currentQuestionIndex].question}`
            );
            props.onProceed();
        } else if (currentQuestionIndex === questions.length - 1) {
            trackPopupClosedEvent(
                'fraud',
                `Question ${currentQuestionIndex} - ${questions[currentQuestionIndex].question}`,
                "my-linked-account|roadblock_click|close",
                `Fraud check_${questions[currentQuestionIndex].question}`
            );
            props.onProceed();
        } else {
            if (currentQuestionIndex === 0)
                trackPopupClickedEvent(
                    'fraud',
                    "Start - " + questions[currentQuestionIndex].question,
                    'Continue',
                    "my-linked-account|roadblock_click|Continue",
                    "Fraud check_"+questions[currentQuestionIndex].question
                );
            else
                trackPopupClickedEvent(
                    'fraud',
                    `Question ${currentQuestionIndex} - ${questions[currentQuestionIndex].question}`,
                    'Continue',
                    "my-linked-account|roadblock_click|Continue",
                    `Fraud check_${questions[currentQuestionIndex].question}`
                );

            nextQuestion();
        }
    };

    return (
        <Modal
            onClose={cancel}
            className="modal--fixed-height modal--wide inactivity-modal modal__mobile-optimised"
            ignoreClickOutside={true}
            mobileOptimised
        >
            {!questions[currentQuestionIndex].intro && (
                <div className="modal-header">
                    <div className="question-index">{`Question ${currentQuestionIndex}`}</div>
                </div>
            )}
            <div>
                {renderQuestion()}
                <hr/>
                <div className="modal-footer">
                    <Button
                        size='med'
                        customClassNames={["pull-left"]}
                        variant='secondary'
                        label='Cancel'
                        onClick={cancel}
                    />
                    <Button
                        size='med'
                        customClassNames={["pull-right"]}
                        label={questions[currentQuestionIndex].intro ? 'Start' : 'Continue'}
                        disabled={selectedAnswer === null && !questions[currentQuestionIndex].intro}
                        onClick={handleProceed}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default withFeatureFlags(UpdateLinkedAccount);
